import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ChevronRight } from "lucide-react";
import { NAV, BIZ } from "@/lib/content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({}); // tracks which submenu groups are open in the mobile drawer
  const loc = useLocation();

  // Close drawer (and reset submenu state) whenever route changes
  useEffect(() => { setOpen(false); setExpanded({}); }, [loc.pathname]);

  // Lock body scroll when the drawer is open so the page underneath doesn't move
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const closeMenu = () => setOpen(false);
  const toggleGroup = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-stone-200" data-testid="site-header">
        <div className="mx-auto max-w-7xl 2xl:max-w-[1440px] px-4 sm:px-5 lg:px-6 2xl:px-8">
          <div className="flex h-20 items-center justify-between gap-3 xl:gap-4 2xl:gap-6">
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
              <div className="w-11 h-11 rounded-lg bg-teal-700 grid place-items-center text-white font-display text-xl font-bold shrink-0">F</div>
              <div className="leading-[1.1]">
                <div className="font-display text-lg font-semibold text-stone-900 whitespace-nowrap tracking-tight">FCA Reports</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-teal-700 font-medium mt-0.5">Australia</div>
              </div>
            </Link>

            {/* DESKTOP NAV — active only ≥1280 (xl). Between 1024-1279 we use the
                mobile drawer so the Request a Free Call CTA never clips at narrow desktop
                widths (verified via testing_agent iter_52 — 1024 previously clipped
                by ~136px). Padding kept minimal at xl to leave room for the enlarged
                NDIS credential (145px wide), normal rhythm at 2xl+. */}
            <nav className="hidden xl:flex items-center gap-0 2xl:gap-1" data-testid="primary-nav">
              {NAV.map((item) => (
                item.groups ? (
                  <div key={item.to} className="relative group">
                    <NavLink to={item.to} className={({ isActive }) => `px-2 2xl:px-3 py-2 rounded-md text-[14.5px] xl:text-[15px] font-medium flex items-center gap-1 whitespace-nowrap ${isActive ? "text-teal-700" : "text-stone-700 hover:text-teal-700"}`} data-testid={`nav-${item.label.toLowerCase()}`}>
                      {item.label} <ChevronDown size={14} />
                    </NavLink>
                    {/* Mega-menu: 3-column grouped layout.
                        Anchored to the LEFT edge of the trigger and opens rightward
                        so it never overflows the viewport left edge (previously
                        used left-1/2 -translate-x-1/2 which clipped when Services
                        sat near the left of the nav). Max-width clamped to the
                        viewport minus a small gutter for the edge case that the
                        trigger sits close to the right edge. */}
                    <div className="absolute left-0 top-full pt-2 hidden group-hover:block" style={{ width: "min(760px, calc(100vw - 32px))" }}>
                      <div className="bg-white border border-stone-200 rounded-xl shadow-xl p-5 grid grid-cols-3 gap-5">
                        {item.groups.map((g) => (
                          <div key={g.heading}>
                            <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-teal-700 mb-2.5">{g.heading}</div>
                            <div className="space-y-1">
                              {g.items.map((c) => (
                                <Link key={c.to} to={c.to} className="block px-2.5 py-1.5 text-[13.5px] text-stone-700 hover:bg-stone-50 hover:text-teal-700 rounded-md" data-testid={`subnav-${c.to.split("/").pop()}`}>{c.label}</Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : item.children ? (
                  <div key={item.to} className="relative group">
                    <NavLink to={item.to} className={({ isActive }) => `px-2 2xl:px-3 py-2 rounded-md text-[14.5px] xl:text-[15px] font-medium flex items-center gap-1 whitespace-nowrap ${isActive ? "text-teal-700" : "text-stone-700 hover:text-teal-700"}`} data-testid={`nav-${item.label.toLowerCase()}`}>
                      {item.label} <ChevronDown size={14} />
                    </NavLink>
                    <div className="absolute left-0 top-full pt-2 hidden group-hover:block min-w-[320px]">
                      <div className="bg-white border border-stone-200 rounded-xl shadow-xl py-2">
                        {item.children.map((c) => (
                          <Link key={c.to} to={c.to} className="block px-4 py-2.5 text-[14px] text-stone-700 hover:bg-stone-50 hover:text-teal-700" data-testid={`subnav-${c.to.split("/").pop()}`}>{c.label}</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <NavLink key={item.to} to={item.to} className={({ isActive }) => `px-2 2xl:px-3 py-2 rounded-md text-[14.5px] xl:text-[15px] font-medium whitespace-nowrap ${isActive ? "text-teal-700" : "text-stone-700 hover:text-teal-700"}`} data-testid={`nav-${item.label.toLowerCase()}`}>
                    {item.label}
                  </NavLink>
                )
              ))}
            </nav>

            {/* RIGHT CLUSTER — active only ≥1280 (xl) to prevent 1024 clipping.
                 xl  (1280+): NDIS + Book (no phone) — medium desktop
                 2xl (1536+): NDIS + Phone + Book — large desktop
                Phone hidden below 2xl. NDIS artwork now 145×~68px (natural 2.128:1
                aspect on a 1781×837 native PNG, so no softness) for prominent readable
                trust credential. `image-rendering:auto` keeps browser at
                highest-quality bicubic downscale. */}
            <div className="hidden xl:flex items-center gap-2 2xl:gap-3 shrink-0">
              <span
                className="inline-flex items-center justify-center shrink-0"
                title="Registered NDIS Provider — Registered with the NDIS Quality and Safeguards Commission"
                data-testid="header-ndis-badge"
              >
                <img
                  src="/images/ndis-registered-provider-440.png"
                  srcSet="/images/ndis-registered-provider-290.webp 290w, /images/ndis-registered-provider-440.webp 440w"
                  sizes="145px"
                  alt="I Heart NDIS — Registered NDIS Provider"
                  className="w-[145px] h-auto shrink-0"
                  style={{ imageRendering: "auto" }}
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                  width="145"
                  height="68"
                  data-testid="header-ndis-artwork"
                />
              </span>
              <a href={BIZ.phoneHref} className="hidden 2xl:inline-flex text-stone-700 hover:text-teal-700 text-sm items-center gap-1.5 font-medium whitespace-nowrap shrink-0" data-testid="header-phone">
                <Phone size={15} className="shrink-0" /> {BIZ.phone}
              </a>
              <Link to="/book-an-appointment" className="bg-teal-700 hover:bg-teal-800 text-white px-4 xl:px-5 py-2.5 rounded-full text-[13.5px] xl:text-sm font-semibold transition whitespace-nowrap shrink-0" data-testid="header-book-btn">
                Request a Free Call
              </Link>
            </div>

            {/* MOBILE TOGGLE — active <1280 (below xl). 44×44 tap target, balanced
                with logo, subtle press feedback */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="xl:hidden inline-flex items-center justify-center w-11 h-11 -mr-1 rounded-full text-stone-800 hover:bg-stone-100 active:bg-stone-200 active:scale-[0.94] transition-all duration-150 motion-reduce:transition-none relative z-[70]"
              data-testid="mobile-menu-toggle"
              aria-expanded={open}
              aria-controls="mobile-nav-drawer"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X size={26} strokeWidth={2} /> : <Menu size={26} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER + BACKDROP — rendered outside the sticky header so it covers
          everything including the bottom sticky CTA bar, and can scroll independently. */}
      <div
        className={`xl:hidden fixed inset-0 z-[60] transition-opacity duration-200 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!open}
        data-testid="mobile-nav-backdrop-wrap"
      >
        {/* Backdrop */}
        <button
          type="button"
          onClick={closeMenu}
          className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
          aria-label="Close menu"
          tabIndex={open ? 0 : -1}
          data-testid="mobile-nav-backdrop"
        />

        {/* Drawer panel — scrolls internally */}
        <nav
          id="mobile-nav-drawer"
          aria-label="Mobile menu"
          className={`absolute top-0 right-0 h-[100dvh] w-[88vw] max-w-sm bg-white shadow-2xl flex flex-col transform transition-transform duration-250 ${open ? "translate-x-0" : "translate-x-full"}`}
          data-testid="mobile-nav"
        >
          {/* Drawer header — slimmer, just logo + close */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-stone-200 shrink-0">
            <Link to="/" onClick={closeMenu} className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-teal-700 grid place-items-center text-white font-display text-base font-bold">F</div>
              <div className="leading-[1.1]">
                <div className="font-display text-[15px] font-semibold text-stone-900 tracking-tight">FCA Reports</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-teal-700 font-medium mt-0.5">Australia</div>
              </div>
            </Link>
            <button type="button" onClick={closeMenu} className="inline-flex items-center justify-center w-11 h-11 -mr-1 rounded-full hover:bg-stone-100 active:bg-stone-200 active:scale-[0.94] transition-all duration-150 motion-reduce:transition-none" aria-label="Close menu" data-testid="mobile-menu-close">
              <X size={22} />
            </button>
          </div>

          {/* Prominent tap-to-call bar — pinned at top of drawer, always visible.
              phone_click Google Ads + GA4 conversion fires automatically via the
              global tel: listener in PixelScripts.jsx — no extra code needed. */}
          <a
            href="tel:0494821041"
            onClick={closeMenu}
            className="group block mx-3 mt-3 mb-1 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-800 text-white px-4 py-3.5 shadow-md hover:shadow-lg active:scale-[0.985] transition-all duration-200 motion-reduce:transition-none"
            data-testid="mobile-nav-tap-call"
            aria-label="Tap to call 0494 821 041 — Speak directly with our team"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/15 grid place-items-center backdrop-blur-sm shrink-0 group-hover:bg-white/20 group-active:bg-white/25 transition-colors duration-200 ring-1 ring-white/10">
                <Phone size={19} strokeWidth={2.25} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                  <span className="font-display text-[16.5px] font-semibold leading-snug tracking-tight">0494 821 041</span>
                  <span className="text-[10.5px] uppercase tracking-[0.16em] text-amber-200/95 font-semibold">— Tap to Call</span>
                </div>
                <div className="text-[12.5px] text-teal-50/90 leading-snug mt-0.5">Speak directly with our NDIS FCA team</div>
              </div>
            </div>
          </a>

          {/* Scrollable link list */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-3" data-testid="mobile-nav-list" style={{ WebkitOverflowScrolling: "touch" }}>
            {NAV.map((item) => {
              if (!item.children && !item.groups) {
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    className="block px-4 py-3 text-[15px] font-medium text-stone-800 rounded-lg hover:bg-stone-50 active:bg-stone-100"
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                );
              }
              const isExpanded = !!expanded[item.to];
              return (
                <div key={item.to} className="border-b border-stone-100 last:border-b-0">
                  <div className="flex items-stretch">
                    <Link
                      to={item.to}
                      onClick={closeMenu}
                      className="flex-1 px-4 py-3 text-[15px] font-medium text-stone-800 rounded-lg hover:bg-stone-50 active:bg-stone-100"
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.to)}
                      className="px-3 text-stone-500 hover:text-teal-700 hover:bg-stone-50 rounded-lg"
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                      data-testid={`mobile-nav-expand-${item.label.toLowerCase()}`}
                    >
                      <ChevronRight size={18} className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="pl-3 pb-2">
                      {item.groups ? (
                        item.groups.map((g) => (
                          <div key={g.heading} className="mb-2">
                            <div className="px-4 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-teal-700">{g.heading}</div>
                            {g.items.map((c) => (
                              <Link
                                key={c.to}
                                to={c.to}
                                onClick={closeMenu}
                                className="block px-4 py-2 text-[13.5px] text-stone-600 hover:text-teal-700 hover:bg-stone-50 active:bg-stone-100 rounded-lg"
                                data-testid={`mobile-subnav-${c.to.split("/").pop()}`}
                              >
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        ))
                      ) : (
                        item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={closeMenu}
                            className="block px-4 py-2.5 text-[13.5px] text-stone-600 hover:text-teal-700 hover:bg-stone-50 active:bg-stone-100 rounded-lg"
                            data-testid={`mobile-subnav-${c.to.split("/").pop()}`}
                          >
                            {c.label}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Drawer footer — primary Book CTA (phone now lives in the prominent top bar) */}
          <div className="px-4 pt-3 pb-5 border-t border-stone-200 shrink-0" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
            <Link to="/book-an-appointment" onClick={closeMenu} className="flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 active:bg-teal-900 text-white py-3.5 rounded-full text-[14.5px] font-semibold shadow-md hover:shadow-lg active:scale-[0.99] transition-all duration-200 motion-reduce:transition-none" data-testid="mobile-nav-book">
              Request a Free Call
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
}
